import React from 'react';
import type { PublicContactSettings, OfficeHourEntry } from '@/lib/contact-settings/schema';

// ─── OfficeHoursList ──────────────────────────────────────────────────────────
// Collapses consecutive identical days into one line, e.g. "Monday – Friday: 9:00 – 18:00 ICT"

interface OfficeHoursListProps {
  officeHours: OfficeHourEntry[];
  timezoneLabel?: string;
  className?: string;
}

export function OfficeHoursList({
  officeHours,
  timezoneLabel = 'ICT',
  className = '',
}: OfficeHoursListProps) {
  if (!officeHours?.length) return null;

  // Group consecutive days with identical open/close/closed
  interface Group {
    startDay: string;
    endDay: string;
    openTime: string;
    closeTime: string;
    isClosed: boolean;
  }

  const groups: Group[] = [];
  for (const entry of officeHours) {
    const last = groups[groups.length - 1];
    if (
      last &&
      last.isClosed === entry.isClosed &&
      last.openTime === entry.openTime &&
      last.closeTime === entry.closeTime
    ) {
      last.endDay = entry.day;
    } else {
      groups.push({
        startDay: entry.day,
        endDay: entry.day,
        openTime: entry.openTime,
        closeTime: entry.closeTime,
        isClosed: entry.isClosed,
      });
    }
  }

  return (
    <div className={`space-y-2 text-sm ${className}`}>
      {groups.map((g, i) => {
        const label = g.startDay === g.endDay ? g.startDay : `${g.startDay} – ${g.endDay}`;
        return (
          <div key={i} className="flex justify-between">
            <span className="text-[#9ca3af]">{label}</span>
            {g.isClosed ? (
              <span className="text-[#9ca3af]">Closed</span>
            ) : (
              <span className="text-white">
                {g.openTime} – {g.closeTime} {timezoneLabel}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── ContactLink ──────────────────────────────────────────────────────────────

interface ContactLinkProps {
  type: 'tel' | 'mailto';
  value: string;
  className?: string;
  children?: React.ReactNode;
}

export function ContactLink({ type, value, className = '', children }: ContactLinkProps) {
  if (!value) return null;
  const href = type === 'tel' ? `tel:${value.replace(/\s/g, '')}` : `mailto:${value}`;
  return (
    <a href={href} className={className}>
      {children ?? value}
    </a>
  );
}

// ─── SocialLinks ──────────────────────────────────────────────────────────────

interface SocialLinksProps {
  socialLinks: PublicContactSettings['socialLinks'];
  className?: string;
  iconClassName?: string;
}

const SOCIAL_ICONS: Record<string, React.ReactNode> = {
  facebook: (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  ),
  instagram: (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
    </svg>
  ),
  linkedin: (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  ),
  x: (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  ),
  youtube: (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  ),
  line: (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
    </svg>
  ),
};

export function SocialLinks({ socialLinks, className = '', iconClassName = '' }: SocialLinksProps) {
  if (!socialLinks) return null;
  const entries = Object.entries(socialLinks).filter(([, v]) => v && v !== '');
  if (!entries.length) return null;

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {entries.map(([platform, url]) => (
        <a
          key={platform}
          href={url as string}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={platform}
          className={`text-[#8b93a3] hover:text-white transition-colors ${iconClassName}`}
        >
          {SOCIAL_ICONS[platform] ?? <span className="text-xs">{platform}</span>}
        </a>
      ))}
    </div>
  );
}

// ─── ContactInfoBlock ─────────────────────────────────────────────────────────

interface ContactInfoBlockProps {
  settings: PublicContactSettings;
}

export function ContactInfoBlock({ settings }: ContactInfoBlockProps) {
  const location = [settings.city, settings.country].filter(Boolean).join(', ');

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-white mb-4">Contact Information</h2>
        <div className="space-y-3">
          {/* Email */}
          <ContactLink
            type="mailto"
            value={settings.supportEmail}
            className="flex items-center gap-4 p-4 rounded-xl border border-white/5 bg-[#111827] hover:border-violet-500/30 transition-colors group"
          >
            <div className="w-10 h-10 rounded-lg bg-violet-500/10 border border-violet-500/20 flex items-center justify-center text-violet-400 flex-shrink-0 group-hover:bg-violet-500/15 transition-colors">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div>
              <p className="text-xs text-[#9ca3af]">Email</p>
              <p className="text-sm font-medium text-white">{settings.supportEmail}</p>
            </div>
          </ContactLink>

          {/* Phone */}
          {settings.phonePrimary && (
            <ContactLink
              type="tel"
              value={settings.phonePrimary}
              className="flex items-center gap-4 p-4 rounded-xl border border-white/5 bg-[#111827] hover:border-violet-500/30 transition-colors group"
            >
              <div className="w-10 h-10 rounded-lg bg-violet-500/10 border border-violet-500/20 flex items-center justify-center text-violet-400 flex-shrink-0 group-hover:bg-violet-500/15 transition-colors">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-xs text-[#9ca3af]">Phone</p>
                <p className="text-sm font-medium text-white">{settings.phonePrimary}</p>
              </div>
            </ContactLink>
          )}

          {/* LINE — the primary business channel in Thailand. Rendered only
              when the owner saved a LINE ID/URL in contact settings. */}
          {(settings.lineId || settings.socialLinks?.line) &&
            (() => {
              const href = settings.lineId?.startsWith('http')
                ? settings.lineId
                : settings.lineId
                  ? `https://line.me/R/ti/p/@${settings.lineId}`
                  : (settings.socialLinks?.line as string);
              return (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 rounded-xl border border-white/5 bg-[#111827] hover:border-[#06C755]/40 transition-colors group"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#06C755]/10 border border-[#06C755]/20 flex items-center justify-center text-[#06C755] flex-shrink-0">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-[#9ca3af]">LINE</p>
                    <p className="text-sm font-medium text-white">Chat with us on LINE</p>
                  </div>
                </a>
              );
            })()}

          {/* Location */}
          {location && (
            <div className="flex items-center gap-4 p-4 rounded-xl border border-white/5 bg-[#111827]">
              <div className="w-10 h-10 rounded-lg bg-violet-500/10 border border-violet-500/20 flex items-center justify-center text-violet-400 flex-shrink-0">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-xs text-[#9ca3af]">Location</p>
                <p className="text-sm font-medium text-white">{location}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Office hours */}
      <div className="rounded-xl border border-white/5 bg-[#111827] p-5">
        <h3 className="text-sm font-semibold text-white mb-3">Office Hours</h3>
        <OfficeHoursList
          officeHours={settings.officeHours}
          timezoneLabel={settings.timezoneLabel}
        />
      </div>

      {/* Quick links */}
      {(settings.demoBookingUrl || settings.pricingUrl || settings.loginUrl) && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-5">
          <h3 className="text-sm font-semibold text-amber-300 mb-3">
            Looking for something specific?
          </h3>
          <div className="space-y-2">
            {settings.demoBookingUrl && (
              <a
                href={settings.demoBookingUrl}
                className="flex items-center gap-2 text-sm text-amber-400/80 hover:text-amber-300 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                Book a product demo
              </a>
            )}
            {settings.pricingUrl && (
              <a
                href={settings.pricingUrl}
                className="flex items-center gap-2 text-sm text-amber-400/80 hover:text-amber-300 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                View pricing plans
              </a>
            )}
            {settings.loginUrl && (
              <a
                href={settings.loginUrl}
                className="flex items-center gap-2 text-sm text-amber-400/80 hover:text-amber-300 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                Sign in to your account
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
